import React, { useState, useEffect, useCallback, useRef } from 'react';
import ErpTemplate from '../../components/ErpTemplate';
import ReactSelect from '../../components/ReactSelect';
import TabelaLoading from '../../components/TabelaLoading';
import TabelaVazia from '../../components/TabelaVazia';
import api from '../../services/api';
import { FaHistory, FaFilter, FaSearch, FaTimes, FaEye, FaFileCsv } from 'react-icons/fa';
import moment from 'moment';
import { toast } from 'react-toastify';
import '../../css/financeiro-parcela-pagar.css';

const PAGE_SIZE_DEFAULT = 50;

const LABEL_EVENTO_AUDIT = {
    created: 'Criado',
    updated: 'Alterado',
    deleted: 'Excluído',
    restored: 'Restaurado',
};

// Chaveado pelo nome da CLASSE auditada (minúsculo), que o log guarda como texto
// em `auditable_type` (ver nomeTabela).
//
// `eventoaereosolicitacao` é AMBÍGUA por decisão de projeto: até 2026_09_11_120000
// ela era o TRECHO mal nomeado, depois passou a ser o PEDIDO. As duas linhas
// carregam o mesmo texto e não há como separá-las pela chave — só pela data. O
// rótulo fica no sentido atual, e a ambiguidade é assumida porque na `dev`
// (produção real) o log nasce no mesmo deploy, sem histórico antigo.
const LABEL_MODULO = {
    participante: 'Participante',
    eventorsvp: 'RSVP do Evento',
    eventocoluna: 'Coluna nomeável (definição)',
    eventoaereosolicitacao: 'Solicitação de Malha Aérea',
    eventoaereoorigem: 'Origem da Solicitação',
    eventoaereotrecho: 'Trecho Aéreo',
    eventomalhaaerea: 'Malha Aérea',
    eventomalhaopcao: 'Opção da Malha',
    eventomalhagrupo: 'Grupo da Malha',
    eventomalhaarquivo: 'Arquivo da Malha',
    fornecedorbanco: 'Conta bancária do fornecedor',
    // A classe é FornecedorClassificacaoFiscal: é esta chave que o nomeTabela() casa.
    // `fornecedorfiscal` fica porque remover chave morta não é trabalho deste merge.
    fornecedorclassificacaofiscal: 'Perfil tributário do fornecedor',
    fornecedorfiscal: 'Perfil tributário do fornecedor',
    fornecedorcontrole: 'Controle cadastral do fornecedor',
    eventoaereo: 'Opção de Aéreo',
    eventoaereoparticipante: 'Opção de Aéreo do Participante',
    eventoaereorequisicao: 'Requisição de Emissão',
    eventoaereoemissao: 'Participante na Emissão de Aéreo',
    eventoaereobilhete: 'Bilhete de Aéreo',
    eventoaereoaba: 'Aba da Planilha de Emissão',
    eventoaereofechamento: 'Faturamento do Aéreo',
    eventoaereoatendimento: 'Atendimento do Aéreo',
    aereofatura: 'Fatura CVC',
    eventocoluna: 'Coluna nomeável (definição)',
    participantecoluna: 'Coluna nomeável (valor)',
};

// Rótulos dos módulos, espelhando o "enum" do back (App\Auditing\ModuloOperacao).
// A LISTA de quais existem em cada domínio NÃO mora aqui — ela vem do back, em
// `dominios[].modulos`, porque é fato estrutural: módulo oferecido no domínio
// errado trocaria a tabela por baixo do admin. Aqui fica só o texto, que é da tela.
const MODULOS = [
    { value: 'rsvp', label: 'RSVP' },
    { value: 'evento', label: 'Evento' },
    { value: 'financeiro', label: 'Financeiro' },
    { value: 'participante', label: 'Participante' },
    { value: 'malhaAerea', label: 'Malha Aérea' },
    // Fornecedor é `participante` com tipoCadastro 'orcamento' + a conta em
    // `fornecedor_banco`: um filtro para as duas tabelas do mesmo domínio.
    { value: 'fornecedor', label: 'Fornecedor' },
    { value: 'opcoesAereo', label: 'Opções de Aéreo' },
    { value: 'emissaoAereo', label: 'Emissão de Aéreo' },
    { value: 'faturaAereo', label: 'Faturas CVC' },
];
const MODULO_ROTULOS = MODULOS.reduce((acc, m) => { acc[m.value] = m.label; return acc; }, {});

// user_type = tipoCadastro de quem fez a ação. Atores que logam e geram auditoria:
// pessoa (interno), participante (convidado), cliente. (Ajustar se surgir outro.)
const TIPOS_USUARIO = [
    { value: 'pessoa', label: 'Pessoa (interno)' },
    { value: 'participante', label: 'Participante (convidado)' },
    { value: 'cliente', label: 'Cliente' },
];

// Tabela = `auditable_type` (o model real gravado pela lib), distinto de Módulo — mas
// NÃO independente dele: cada model grava um `modulo` fixo no seu auditModulo(). Por
// isso o `modulo` vem declarado junto aqui: é o que concilia os dois seletores, e sem
// ele dá para pedir módulo de um domínio com tabela de outro, que nunca retorna linha.
const TABELAS = [
    { value: 'App\\Models\\Participante\\Participante', label: 'Participante', modulo: 'participante' },
    { value: 'App\\Models\\Evento\\EventoRsvp', label: 'RSVP do Evento', modulo: 'rsvp' },
    { value: 'App\\Models\\Evento\\EventoAereoSolicitacao', label: 'Solicitação de Malha Aérea', modulo: 'malhaAerea' },
    { value: 'App\\Models\\Evento\\EventoAereoOrigem', label: 'Origem da Solicitação', modulo: 'malhaAerea' },
    { value: 'App\\Models\\Evento\\EventoAereoTrecho', label: 'Trecho Aéreo', modulo: 'malhaAerea' },
    { value: 'App\\Models\\Evento\\EventoMalhaAerea', label: 'Malha Aérea', modulo: 'malhaAerea' },
    { value: 'App\\Models\\Evento\\EventoMalhaOpcao', label: 'Opção da Malha', modulo: 'malhaAerea' },
    { value: 'App\\Models\\Evento\\EventoMalhaGrupo', label: 'Grupo da Malha', modulo: 'malhaAerea' },
    { value: 'App\\Models\\Evento\\EventoMalhaArquivo', label: 'Arquivo da Malha', modulo: 'malhaAerea' },
    { value: 'App\\Models\\Evento\\EventoAereo', label: 'Opção de Aéreo', modulo: 'opcoesAereo' },
    // Vínculo N:N da importação para participantes: importar ou liberar cria a linha, retirar a apaga.
    { value: 'App\\Models\\Evento\\EventoAereoParticipante', label: 'Opção de Aéreo do Participante', modulo: 'opcoesAereo' },
    { value: 'App\\Models\\Evento\\EventoAereoRequisicao', label: 'Requisição de Emissão', modulo: 'emissaoAereo' },
    { value: 'App\\Models\\Evento\\EventoAereoEmissao', label: 'Participante na Emissão', modulo: 'emissaoAereo' },
    { value: 'App\\Models\\Evento\\EventoAereoBilhete', label: 'Bilhete de Aéreo', modulo: 'emissaoAereo' },
    { value: 'App\\Models\\Evento\\EventoAereoAba', label: 'Aba da Planilha de Emissão', modulo: 'emissaoAereo' },
    { value: 'App\\Models\\Evento\\EventoAereoFechamento', label: 'Faturamento do Aéreo', modulo: 'emissaoAereo' },
    { value: 'App\\Models\\Evento\\EventoAereoAtendimento', label: 'Atendimento do Aéreo', modulo: 'malhaAerea' },
    { value: 'App\\Models\\Evento\\AereoFatura', label: 'Fatura CVC', modulo: 'faturaAereo' },
    // EventoColuna grava ModuloOperacao::EVENTO; ParticipanteColuna não é auditada e não tem módulo.
    { value: 'App\\Models\\Evento\\EventoColuna', label: 'Coluna nomeável (definição)', modulo: 'evento' },
    { value: 'App\\Models\\Participante\\ParticipanteColuna', label: 'Coluna nomeável (valor)' },
    // Ambos declaram ModuloOperacao::FORNECEDOR no auditModulo().
    { value: 'App\\Models\\Fornecedor\\FornecedorBanco', label: 'Conta bancária do fornecedor', modulo: 'fornecedor' },
    { value: 'App\\Models\\Fornecedor\\FornecedorClassificacaoFiscal', label: 'Perfil tributário do fornecedor', modulo: 'fornecedor' },
];

// Campos avançados (dentro do modal) — usados para contar filtros ativos e limpar.
const CAMPOS_AVANCADOS = ['modulo', 'auditable_type', 'event', 'user_type', 'user_id', 'evento', 'participante', 'campos', 'auditable_id', 'dataInicial', 'dataFinal'];

const nomeTabela = (auditableType) => {
    if (!auditableType) return '-';
    const base = auditableType.split('\\').pop();
    return LABEL_MODULO[base.toLowerCase()] || base;
};

const formatarValor = (valor) => {
    if (valor === null || valor === undefined || valor === '') return '(vazio)';
    if (typeof valor === 'object') return JSON.stringify(valor);
    return String(valor);
};

// Campos cujo valor no diff é um id de status de participante (participante.situacaoevento).
// A auditoria grava o id cru, que não diz nada a quem lê — o (i) traduz no hover.
const CAMPOS_SITUACAO = ['situacaoevento'];

/**
 * Valor de uma célula do diff. Em campo de status, acrescenta o (i) com o nome no hover.
 * Só front: nada muda no que o back grava em log_operacao.
 */
const ValorAudit = ({ campo, valor, situacoes }) => {
    const texto = formatarValor(valor);
    const ehStatus = CAMPOS_SITUACAO.indexOf(campo) !== -1;

    if (!ehStatus || valor === null || valor === undefined || valor === '') {
        return <>{texto}</>;
    }

    const nome = situacoes[String(valor)];

    return (
        <>
            {texto}
            <span
                title={nome || 'Status não encontrado — pode ter sido excluído depois desta alteração'}
                style={{ cursor: 'help', marginLeft: 6, opacity: 0.75, fontWeight: 600 }}
            >
                (i)
            </span>
        </>
    );
};

// Deixa o User-Agent legível: "Chrome 149 · Windows 10/11".
const normalizarUserAgent = (ua) => {
    if (!ua) return '-';
    let navegador = 'Desconhecido';
    const mEdge = ua.match(/Edg\/(\d+)/);
    const mOpera = ua.match(/OPR\/(\d+)/);
    const mChrome = ua.match(/Chrome\/(\d+)/);
    const mFirefox = ua.match(/Firefox\/(\d+)/);
    const mSafari = ua.match(/Version\/(\d+).*Safari/);
    if (mEdge) navegador = 'Edge ' + mEdge[1];
    else if (mOpera) navegador = 'Opera ' + mOpera[1];
    else if (mChrome) navegador = 'Chrome ' + mChrome[1];
    else if (mFirefox) navegador = 'Firefox ' + mFirefox[1];
    else if (mSafari) navegador = 'Safari ' + mSafari[1];

    let so = '';
    if (/Windows NT 10/.test(ua)) so = 'Windows 10/11';
    else if (/Windows NT/.test(ua)) so = 'Windows';
    else if (/Android/.test(ua)) so = 'Android';
    else if (/iPhone|iPad|iPod/.test(ua)) so = 'iOS';
    else if (/Mac OS X/.test(ua)) so = 'macOS';
    else if (/Linux/.test(ua)) so = 'Linux';

    return so ? navegador + ' · ' + so : navegador;
};

export default function PainelAuditLog() {
    const token = localStorage.getItem('token');
    const perfil = localStorage.getItem('perfil');
    const [filtro, setFiltro] = useState({});
    const [registros, setRegistros] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, pageSize: PAGE_SIZE_DEFAULT, total: 0 });

    // Domínios (abas) e qual está ativo. Ambos vêm do back: `dominios` monta as
    // abas — domínio novo aparece sozinho, sem tocar aqui — e `dominio` diz em qual
    // tabela a consulta caiu, que a tela não teria como adivinhar quando o domínio
    // foi derivado do módulo escolhido em vez de clicado.
    const [dominios, setDominios] = useState([]);
    const [dominioAtivo, setDominioAtivo] = useState(null);

    // Onde cada módulo mora: a pertinência vem do back, em `dominios[].modulos`.
    // Os dois seletores oferecem TODAS as opções, sempre — escopá-los ao domínio
    // ativo escondia o Aéreo de quem estava na aba Operação, e escolher módulo ou
    // tabela é justamente o caminho para mudar de domínio. Aqui só se descobre qual.
    const dominioDoModulo = (modulo) => {
        const dono = modulo && dominios.find((d) => Array.isArray(d.modulos) && d.modulos.includes(modulo));
        return dono ? dono.valor : null;
    };
    const moduloDaTabela = (tipo) => {
        const t = tipo && TABELAS.find((x) => x.value === tipo);
        return t ? t.modulo : null;
    };

    // Domínio do recorte: a tabela é mais específica que o módulo e vence; sem
    // nenhum dos dois, vale a aba. Resolver num lugar só é o que impede a busca de
    // sair contraditória — e é o que faz a aba seguir o filtro no Aplicar.
    const dominioDoRecorte = () => (
        dominioDoModulo(moduloDaTabela(filtro.auditable_type))
        || dominioDoModulo(filtro.modulo)
        || dominioAtivo
        || undefined
    );
    const [buscando, setBuscando] = useState(false);
    const [detalhe, setDetalhe] = useState(null);
    const [mostrarFiltros, setMostrarFiltros] = useState(false);
    const [situacoes, setSituacoes] = useState({});
    const controllerRef = useRef(null);

    // Todos os status de participante, uma vez só, para o (i) do diff. SEM filtro de
    // situacaoRegistro de propósito: entrada antiga pode apontar para status já desativado, e é
    // justamente essa que ninguém consegue ler. O endpoint devolve tudo (get(), sem paginação).
    useEffect(() => {
        api({
            method: 'get',
            action: '/eventoParticipanteSituacao',
            token: token,
        })
            .then((response) => {
                const lista = ((response || {}).data || {}).data || [];
                const mapa = {};
                lista.forEach((registro) => { mapa[String(registro.id)] = registro.nome; });
                setSituacoes(mapa);
            })
            .catch(() => {
                // Sem os nomes o (i) avisa que não achou — a auditoria não depende disso para abrir.
            });
    }, [token]);

    const buscarUsuarios = (input) => {
        return api({
            method: 'get',
            action: '/participante',
            token: token,
            params: {
                nome: input,
                tipoCadastro: 'pessoa',
                situacaoRegistro: 'ativo',
            },
        }).then((response) => (response.data.data || []).map((registro) => ({
            value: registro.id,
            label: (registro.codigo ? registro.codigo + ' - ' : '') + registro.nome,
        })));
    };

    // Mesmo comportamento do search do Painel de Eventos: o param `nome` busca
    // por nome, identificação ou código (EventoController::index).
    const buscarEventos = (input) => {
        return api({
            method: 'get',
            action: '/evento',
            token: token,
            params: { nome: input },
        }).then((response) => (response.data.data || []).map((registro) => ({
            value: registro.id,
            label: (registro.codigo ? registro.codigo + ' - ' : '')
                + (registro.identificacao ? registro.identificacao + ' - ' : '')
                + (registro.nome || ''),
        })));
    };

    // Participantes; se um evento estiver selecionado, escopa a busca a ele
    // (ParticipanteController::index filtra por `evento`/`eventos`).
    const buscarParticipantes = (input) => {
        return api({
            method: 'get',
            action: '/participante',
            token: token,
            params: {
                nome: input,
                tipoCadastro: 'participante',
                evento: filtro.evento || undefined,
            },
        }).then((response) => (response.data.data || []).map((registro) => ({
            value: registro.id,
            label: (registro.codigo ? registro.codigo + ' - ' : '') + registro.nome,
        })));
    };

    // Mesmo recorte da tabela vai para o CSV: um único lugar monta os filtros, senão o relatório
    // passa a divergir do que está na tela sem ninguém perceber.
    const filtrosDaBusca = (dominioForcado) => ({
        busca: filtro.busca || undefined,
        // Qual tabela de log consultar. O clique na aba passa o domínio direto,
        // porque o estado dela só estaria novo no render seguinte.
        dominio: dominioForcado || dominioDoRecorte(),
        // Na troca de aba o `filtro` ainda tem o módulo e a tabela do domínio anterior, que não existem
        // no novo: a aba limpa os dois no estado, e aqui eles também não vão.
        modulo: dominioForcado ? undefined : (filtro.modulo || undefined),
        auditable_type: dominioForcado ? undefined : (filtro.auditable_type || undefined),
        event: filtro.event || undefined,
        user_id: filtro.user_id || undefined,
        user_type: filtro.user_type || undefined,
        evento: filtro.evento || undefined,
        participante: filtro.participante || undefined,
        campos: filtro.campos || undefined,
        auditable_id: filtro.auditable_id || undefined,
        dataInicial: filtro.dataInicial || undefined,
        dataFinal: filtro.dataFinal || undefined,
    });

    const [exportando, setExportando] = useState(false);
    const [mostrarExport, setMostrarExport] = useState(false);
    const [opcoesExport, setOpcoesExport] = useState({ incluirCampos: true, formato: 'unificado' });
    // Filtro da última busca APLICADA: o export tem que sair com o mesmo recorte da tabela. Usar o
    // `filtro` vivo faria o CSV levar o que está sendo digitado, enquanto a tela mostra o anterior.
    const filtroAplicadoRef = useRef(null);

    // Exporta o recorte INTEIRO (sem paginação) — é relatório, não a página visível.
    const exportarCsv = async () => {
        setMostrarExport(false);
        setExportando(true);
        try {
            const resp = await api({
                method: 'get',
                action: '/logOperacaoExportar',
                token,
                params: {
                    ...(filtroAplicadoRef.current ?? filtrosDaBusca()),
                    incluir_campos: opcoesExport.incluirCampos ? 1 : 0,
                    formato_campos: opcoesExport.formato,
                },
                responseType: 'blob',
            });

            const cd = resp.headers['content-disposition'] || '';
            const m = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)/i);
            let nomeArquivo = 'auditoria.csv';
            if (m) {
                try { nomeArquivo = decodeURIComponent(m[1]); } catch (_) { nomeArquivo = m[1].replace(/['"]/g, ''); }
            }

            const url = window.URL.createObjectURL(resp.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = nomeArquivo;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            // Com responseType blob o erro do backend também chega como Blob: lê de volta para texto
            // antes de desistir, senão um "acesso negado" vira mensagem genérica.
            let mensagem = 'Não foi possível exportar a auditoria.';
            try {
                const texto = await error?.response?.data?.text?.();
                if (texto) {
                    const json = JSON.parse(texto);
                    mensagem = json.msg || json.error || mensagem;
                }
            } catch (_) { /* mantém a mensagem genérica */ }
            toast.error(mensagem);
        } finally {
            setExportando(false);
        }
    };

    const buscar = useCallback(({ page = 1, pageSize = pagination.pageSize, dominio } = {}) => {
        if (controllerRef.current) controllerRef.current.abort();
        controllerRef.current = new AbortController();
        setBuscando(true);
        const recorte = filtrosDaBusca(dominio);
        const params = {
            ...recorte,
            page,
            pageSize,
        };

        api({
            method: 'get',
            action: '/logOperacao',
            token: token,
            params,
            signal: controllerRef.current.signal,
        })
            .then((response) => {
                // Só o RECORTE: `page`/`pageSize` ficam de fora porque o export é o filtro inteiro,
                // sem paginação. Hoje o backend os ignoraria, mas mandá-los deixaria o relatório
                // pronto para truncar sozinho no dia em que alguém paginar o `exportar`.
                filtroAplicadoRef.current = recorte;
                setRegistros(response.data.data || []);
                setPagination({
                    page: response.data.current_page || page,
                    pageSize: response.data.per_page || pageSize,
                    total: response.data.total || 0,
                });
                if (Array.isArray(response.data.dominios)) setDominios(response.data.dominios);
                if (response.data.dominio) setDominioAtivo(response.data.dominio);
                setBuscando(false);
            })
            .catch((err) => {
                if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError') return;
                setRegistros([]);
                // Zera o total junto: ele é quem habilita o "Exportar CSV". Sem isto a busca que
                // falhou deixava a tabela vazia e o botão vivo, baixando o recorte ANTERIOR — num
                // relatório de auditoria, pior do que não exportar.
                setPagination((atual) => ({ ...atual, page, total: 0 }));
                // E avisa, senão a tabela vazia + o "Nenhum registro no filtro atual" do botão
                // afirmam que o filtro não achou nada, quando na verdade a busca falhou.
                toast.error('Erro ao buscar: ' + (err.response?.data?.error ?? err.message ?? ''));
                setBuscando(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtro, pagination.pageSize, token]);

    useEffect(() => {
        if (perfil === 'admin') {
            buscar({ page: 1 });
        }
        return () => { if (controllerRef.current) controllerRef.current.abort(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (perfil !== 'admin') {
        return (
            <ErpTemplate>
                <div className="main-content container-fluid fin-pp">
                    <div className="fin-pp__card fin-pp__empty" style={{ padding: 24 }}>Acesso negado.</div>
                </div>
            </ErpTemplate>
        );
    }

    const limparFiltros = () => {
        setFiltro((atual) => (atual.busca ? { busca: atual.busca } : {}));
    };

    const filtrosAvancadosAtivos = CAMPOS_AVANCADOS.filter((k) => filtro[k]).length;

    const camposResumo = (campos) => {
        if (!Array.isArray(campos) || campos.length === 0) return '-';
        const visiveis = campos.slice(0, 3);
        const resto = campos.length - visiveis.length;
        return visiveis.join(', ') + (resto > 0 ? ` +${resto}` : '');
    };

    return (
        <ErpTemplate>
            <div className="main-content container-fluid fin-pp">
                <div className="fin-pp__head">
                    <div>
                        <h1 className="fin-pp__title">Log de Auditoria</h1>
                        <p className="fin-pp__subtitle">
                            {dominioAtivo === 'aereo'
                                ? 'Histórico de alterações do Aéreo'
                                : 'Histórico de alterações do sistema'}
                        </p>
                    </div>
                    <div className="fin-pp__actions">
                        <button
                            type="button"
                            className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--sm"
                            onClick={() => setMostrarExport(true)}
                            disabled={exportando || pagination.total === 0}
                            title={pagination.total === 0
                                ? 'Nenhum registro no filtro atual'
                                : 'Exporta todos os ' + pagination.total + ' registros do filtro (sem paginação)'}
                        >
                            <FaFileCsv size={14} /> {exportando ? 'Exportando...' : 'Exportar CSV'}
                        </button>
                    </div>
                </div>

                {/* Uma aba por DOMÍNIO, e domínio é uma tabela de log. A lista vem do
                    back, então domínio novo aparece aqui sozinho. Trocar de aba limpa
                    o módulo: os módulos são escopados por domínio, e manter o anterior
                    filtraria por um valor que não existe na tabela nova. */}
                {dominios.length > 1 && (
                    <div className="fin-pp__tabs mb-3">
                        {dominios.map((d) => (
                            <button
                                key={d.valor}
                                type="button"
                                className={'fin-pp__tab' + (dominioAtivo === d.valor ? ' is-active' : '')}
                                onClick={() => {
                                    // Trocar de aba é trocar de TABELA de log: módulo e tabela do
                                    // domínio anterior não existem no novo. O domínio vai explícito
                                    // porque `buscar` fecha sobre o `filtro`, que só estaria novo no
                                    // render seguinte.
                                    setDominioAtivo(d.valor);
                                    setFiltro({ ...filtro, modulo: undefined, auditable_type: undefined });
                                    buscar({ page: 1, dominio: d.valor });
                                }}
                            >
                                {d.rotulo}
                            </button>
                        ))}
                    </div>
                )}

                <form className="fin-pp__card fin-pp__search" style={{ padding: '14px 16px' }} onSubmit={(e) => { e.preventDefault(); buscar({ page: 1 }); }}>
                    <div className="fin-pp__search-field">
                        <FaSearch className="fin-pp__search-ic" size={16} />
                        <input
                            type="text"
                            className="fin-pp__input"
                            placeholder="Buscar por participante, evento, IP, valor alterado, URL, método…"
                            value={filtro.busca ?? ''}
                            onChange={(e) => setFiltro({ ...filtro, busca: e.target.value })}
                        />
                    </div>
                    <button type="submit" className="fin-pp__btn fin-pp__btn--primary fin-pp__btn--tall">
                        <FaSearch size={14} /> Buscar
                    </button>
                    <button type="button" className="fin-pp__btn fin-pp__btn--tall" onClick={() => setMostrarFiltros(true)}>
                        <FaFilter size={14} /> Mais filtros{filtrosAvancadosAtivos > 0 ? ` (${filtrosAvancadosAtivos})` : ''}
                    </button>
                </form>

                <div className="fin-pp__card mt-4" style={{ overflow: 'hidden' }}>
                    <div className="fin-pp__tblwrap">
                        <table className="fin-pp__tbl" style={{ minWidth: 1100, tableLayout: 'auto' }}>
                            <thead>
                                <tr>
                                    <th style={{ width: 140 }}>Data/Hora</th>
                                    <th style={{ width: 90 }}>Ação</th>
                                    <th style={{ width: 130 }}>Tabela</th>
                                    <th style={{ width: 110 }}>Módulo</th>
                                    <th style={{ width: 140 }}>Registro</th>
                                    <th style={{ width: 180 }}>Participante</th>
                                    <th style={{ width: 160 }}>Usuário</th>
                                    <th style={{ width: 110 }}>Tipo de Usuário</th>
                                    <th style={{ width: 180 }}>Evento</th>
                                    <th>Campos alterados</th>
                                    <th style={{ width: 110 }}>IP</th>
                                    <th style={{ width: 70 }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {buscando ? (
                                    <tr><td colSpan={12} className="fin-pp__empty" style={{ padding: 0 }}><TabelaLoading /></td></tr>
                                ) : registros.length === 0 ? (
                                    <tr><td colSpan={12} className="fin-pp__empty" style={{ padding: 0 }}><TabelaVazia /></td></tr>
                                ) : registros.map((row) => (
                                    <tr key={row.id}>
                                        <td className="fin-pp__cell-muted">{row.created_at ? moment(row.created_at).format('DD/MM/YYYY HH:mm') : '-'}</td>
                                        <td>{LABEL_EVENTO_AUDIT[row.event] || row.event}</td>
                                        <td>{nomeTabela(row.auditable_type)}</td>
                                        <td className="fin-pp__cell-muted">{MODULO_ROTULOS[row.modulo] || row.modulo || '-'}</td>
                                        <td className="fin-pp__cell-muted">{row.auditable_id}</td>
                                        <td className="fin-pp__cell-muted">{row.participante_dados ? ((row.participante_dados.codigo ? row.participante_dados.codigo + ' - ' : '') + row.participante_dados.nome) : '-'}</td>
                                        <td>{row.user ? row.user.nome : (row.user_id || '-')}</td>
                                        <td className="fin-pp__cell-muted">{row.user_type || '-'}</td>
                                        <td className="fin-pp__cell-muted">
                                            {row.evento_dados
                                                ? (row.evento_dados.identificacao ? row.evento_dados.identificacao + ' - ' : '') + (row.evento_dados.nome || '')
                                                : (row.evento || '-')}
                                        </td>
                                        <td className="fin-pp__cell-muted">{camposResumo(row.campos)}</td>
                                        <td className="fin-pp__cell-muted">{row.ip_address || '-'}</td>
                                        <td>
                                            <button
                                                type="button"
                                                className="fin-pp__btn fin-pp__btn--sm"
                                                onClick={() => setDetalhe(row)}
                                                title="Ver detalhes"
                                            >
                                                <FaEye size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {pagination.total > 0 && (() => {
                        const totalPaginas = Math.max(1, Math.ceil(pagination.total / pagination.pageSize));
                        const p = pagination.page;
                        return (
                            <div className="fin-pp__pagination">
                                <div className="fin-pp__pagination-info">
                                    {((p - 1) * pagination.pageSize) + 1}–{Math.min(p * pagination.pageSize, pagination.total)} de {pagination.total}
                                </div>
                                <div className="fin-pp__pagination-ctrls">
                                    <select className="fin-pp__select" style={{ height: 34 }} value={pagination.pageSize} onChange={(e) => buscar({ page: 1, pageSize: Number(e.target.value) })}>
                                        {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n} / pág.</option>)}
                                    </select>
                                    <button type="button" className="fin-pp__btn fin-pp__btn--sm" disabled={p <= 1} onClick={() => buscar({ page: p - 1 })}>Anterior</button>
                                    <span className="fin-pp__pagination-info">{p} / {totalPaginas}</span>
                                    <button type="button" className="fin-pp__btn fin-pp__btn--sm" disabled={p >= totalPaginas} onClick={() => buscar({ page: p + 1 })}>Próxima</button>
                                </div>
                            </div>
                        );
                    })()}
                </div>

                {/* Modal — filtros avançados */}
                <div
                    className={`modal fade ${mostrarFiltros ? 'show d-block' : ''} fin-pp fin-pp__modalbg`}
                    style={{ display: mostrarFiltros ? 'block' : 'none' }}
                    onClick={(e) => { if (e.target === e.currentTarget) setMostrarFiltros(false); }}
                >
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content fin-pp__modal">
                            <div className="fin-pp__modal-head">
                                <span className="fin-pp__modal-head-ic"><FaFilter size={17} /></span>
                                <h3 className="fin-pp__modal-title">Filtros avançados
                                    <span className="fin-pp__modal-sub">Refine o log de auditoria</span>
                                </h3>
                                <button type="button" className="fin-pp__modal-close" onClick={() => setMostrarFiltros(false)}><FaTimes size={16} /></button>
                            </div>
                            <div className="modal-body">
                                <div className="fin-pp__filters" style={{ alignItems: 'stretch' }}>
                                    <div className="fin-pp__field">
                                        <label className="fin-pp__label">Módulo</label>
                                        <select
                                            className="fin-pp__select"
                                            value={filtro.modulo || ''}
                                            onChange={(e) => {
                                                const modulo = e.target.value || undefined;
                                                // Só registra a escolha — a aba e a busca só mudam no Aplicar.
                                                // Tabela que não pertence ao módulo escolhido sai junto, senão o
                                                // recorte pediria duas coisas incompatíveis e voltaria vazio.
                                                const casa = !modulo || moduloDaTabela(filtro.auditable_type) === modulo;
                                                setFiltro({ ...filtro, modulo, auditable_type: casa ? filtro.auditable_type : undefined });
                                            }}
                                        >
                                            <option value="">Todos</option>
                                            {MODULOS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                                        </select>
                                    </div>
                                    <div className="fin-pp__field">
                                        <label className="fin-pp__label">Ação</label>
                                        <select
                                            className="fin-pp__select"
                                            value={filtro.event || ''}
                                            onChange={(e) => setFiltro({ ...filtro, event: e.target.value || undefined })}
                                        >
                                            <option value="">Todas</option>
                                            <option value="created">Criado</option>
                                            <option value="updated">Alterado</option>
                                            <option value="deleted">Excluído</option>
                                            <option value="restored">Restaurado</option>
                                        </select>
                                    </div>
                                    <div className="fin-pp__field">
                                        <label className="fin-pp__label">Tipo de Usuário</label>
                                        <select
                                            className="fin-pp__select"
                                            value={filtro.user_type || ''}
                                            onChange={(e) => setFiltro({ ...filtro, user_type: e.target.value || undefined })}
                                        >
                                            <option value="">Todos</option>
                                            {TIPOS_USUARIO.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                        </select>
                                    </div>
                                    <div className="fin-pp__field" style={{ minWidth: 220 }}>
                                        <label className="fin-pp__label">Usuário</label>
                                        <ReactSelect
                                            isClearable
                                            defaultOptions={false}
                                            placeholder="Buscar por nome..."
                                            value={filtro.user_id ? { value: filtro.user_id, label: filtro.nomeUsuario } : null}
                                            onChange={(e) => setFiltro({ ...filtro, user_id: e?.value ?? undefined, nomeUsuario: e?.label ?? undefined })}
                                            loadOptions={buscarUsuarios}
                                        />
                                    </div>
                                    <div className="fin-pp__field" style={{ minWidth: 220 }}>
                                        <label className="fin-pp__label">Evento</label>
                                        <ReactSelect
                                            isClearable
                                            defaultOptions={false}
                                            placeholder="Buscar evento..."
                                            value={filtro.evento ? { value: filtro.evento, label: filtro.nomeEvento } : null}
                                            onChange={(e) => setFiltro({ ...filtro, evento: e?.value ?? undefined, nomeEvento: e?.label ?? undefined, participante: undefined, nomeParticipante: undefined })}
                                            loadOptions={buscarEventos}
                                        />
                                    </div>
                                    <div className="fin-pp__field" style={{ minWidth: 220 }}>
                                        <label className="fin-pp__label">Participante</label>
                                        <ReactSelect
                                            key={'part-' + (filtro.evento || 'todos')}
                                            isClearable
                                            defaultOptions={false}
                                            placeholder={filtro.evento ? 'Participantes do evento...' : 'Buscar por nome...'}
                                            value={filtro.participante ? { value: filtro.participante, label: filtro.nomeParticipante } : null}
                                            onChange={(e) => setFiltro({ ...filtro, participante: e?.value ?? undefined, nomeParticipante: e?.label ?? undefined })}
                                            loadOptions={buscarParticipantes}
                                        />
                                    </div>
                                    <div className="fin-pp__field">
                                        <label className="fin-pp__label">Tabela</label>
                                        <select
                                            className="fin-pp__select"
                                            value={filtro.auditable_type || ''}
                                            onChange={(e) => {
                                                const tipo = e.target.value || undefined;
                                                // A tabela conhece o módulo dela, então escolhê-la preenche o
                                                // Módulo junto. A aba e a busca só mudam no Aplicar.
                                                setFiltro({ ...filtro, auditable_type: tipo, modulo: moduloDaTabela(tipo) || filtro.modulo });
                                            }}
                                        >
                                            <option value="">Todas</option>
                                            {TABELAS.filter(t=>(!filtro.modulo)||filtro.modulo==t.modulo).map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                        </select>
                                    </div>
                                    <div className="fin-pp__field">
                                        <label className="fin-pp__label">Campos alterados</label>
                                        <input
                                            type="text"
                                            className="fin-pp__input"
                                            style={{ paddingLeft: 14 }}
                                            placeholder="ex.: nome, crm, datacartaconvite"
                                            value={filtro.campos || ''}
                                            onChange={(e) => setFiltro({ ...filtro, campos: e.target.value || undefined })}
                                        />
                                    </div>
                                    <div className="fin-pp__field">
                                        <label className="fin-pp__label">Registro (id)</label>
                                        <input
                                            type="text"
                                            className="fin-pp__input"
                                            style={{ paddingLeft: 14 }}
                                            value={filtro.auditable_id || ''}
                                            onChange={(e) => setFiltro({ ...filtro, auditable_id: e.target.value || undefined })}
                                        />
                                    </div>
                                    <div className="fin-pp__field" style={{ minWidth: 280 }}>
                                        <label className="fin-pp__label">Período</label>
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <input
                                                type="date"
                                                className="fin-pp__input"
                                                style={{ paddingLeft: 14 }}
                                                title="Data inicial"
                                                value={filtro.dataInicial || ''}
                                                onChange={(e) => setFiltro({ ...filtro, dataInicial: e.target.value || undefined })}
                                            />
                                            <input
                                                type="date"
                                                className="fin-pp__input"
                                                style={{ paddingLeft: 14 }}
                                                title="Data final"
                                                value={filtro.dataFinal || ''}
                                                min={filtro.dataInicial || undefined}
                                                onChange={(e) => setFiltro({ ...filtro, dataFinal: e.target.value || undefined })}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="fin-pp__modal-foot">
                                <button type="button" className="fin-pp__btn" onClick={limparFiltros}>Limpar filtros</button>
                                <button type="button" className="fin-pp__btn fin-pp__btn--primary" onClick={() => { setMostrarFiltros(false); buscar({ page: 1 }); }}>
                                    <FaSearch size={14} /> Aplicar filtros
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Modal — opções do export */}
                {mostrarExport && (
                    <div
                        className="modal fade show d-block fin-pp fin-pp__modalbg"
                        onClick={(e) => { if (e.target === e.currentTarget) setMostrarExport(false); }}
                    >
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content fin-pp__modal">
                                <div className="fin-pp__modal-head">
                                    <span className="fin-pp__modal-head-ic"><FaFileCsv size={17} /></span>
                                    <h3 className="fin-pp__modal-title">Exportar CSV
                                        <span className="fin-pp__modal-sub">{pagination.total} registros do filtro atual</span>
                                    </h3>
                                    <button type="button" className="fin-pp__modal-close" onClick={() => setMostrarExport(false)}><FaTimes size={16} /></button>
                                </div>
                                <div className="modal-body">
                                    <div className="fin-pp__checks">
                                        <label className="fin-pp__check-row">
                                            <input
                                                type="checkbox"
                                                className="fin-pp__check"
                                                checked={opcoesExport.incluirCampos}
                                                onChange={(e) => setOpcoesExport({ ...opcoesExport, incluirCampos: e.target.checked })}
                                            />
                                            <span className="fin-pp__check-label">Incluir uma coluna por campo alterado</span>
                                        </label>
                                        <label className={`fin-pp__check-row ${opcoesExport.incluirCampos ? '' : 'is-disabled'}`} style={{ paddingLeft: 24 }}>
                                            <input
                                                type="radio"
                                                className="fin-pp__check"
                                                disabled={!opcoesExport.incluirCampos}
                                                checked={opcoesExport.formato === 'unificado'}
                                                onChange={() => setOpcoesExport({ ...opcoesExport, formato: 'unificado' })}
                                            />
                                            <span className="fin-pp__check-label">Antes e depois na mesma célula (antes → depois)</span>
                                        </label>
                                        <label className={`fin-pp__check-row ${opcoesExport.incluirCampos ? '' : 'is-disabled'}`} style={{ paddingLeft: 24 }}>
                                            <input
                                                type="radio"
                                                className="fin-pp__check"
                                                disabled={!opcoesExport.incluirCampos}
                                                checked={opcoesExport.formato === 'separado'}
                                                onChange={() => setOpcoesExport({ ...opcoesExport, formato: 'separado' })}
                                            />
                                            <span className="fin-pp__check-label">Antes e depois em colunas separadas</span>
                                        </label>
                                    </div>
                                </div>
                                <div className="fin-pp__modal-foot">
                                    <button type="button" className="fin-pp__btn" onClick={() => setMostrarExport(false)}>Cancelar</button>
                                    <button type="button" className="fin-pp__btn fin-pp__btn--primary" onClick={exportarCsv}>
                                        <FaFileCsv size={14} /> Exportar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal — detalhe do registro */}
                {detalhe && (
                    <div
                        className="modal fade show d-block fin-pp fin-pp__modalbg"
                        onClick={(e) => { if (e.target === e.currentTarget) setDetalhe(null); }}
                    >
                        <div className="modal-dialog modal-dialog-centered modal-lg">
                            <div className="modal-content fin-pp__modal">
                                <div className="fin-pp__modal-head">
                                    <span className="fin-pp__modal-head-ic"><FaHistory size={17} /></span>
                                    <h3 className="fin-pp__modal-title">
                                        {nomeTabela(detalhe.auditable_type)} — {LABEL_EVENTO_AUDIT[detalhe.event] || detalhe.event}
                                        <span className="fin-pp__modal-sub">
                                            {detalhe.created_at ? moment(detalhe.created_at).format('DD/MM/YYYY HH:mm') : '-'}
                                            {' · '}{detalhe.user ? detalhe.user.nome : (detalhe.user_id || '-')}
                                        </span>
                                    </h3>
                                    <button type="button" className="fin-pp__modal-close" onClick={() => setDetalhe(null)}><FaTimes size={16} /></button>
                                </div>
                                <div className="modal-body" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                                    <div className="fin-pp__filters" style={{ marginBottom: 16, flexDirection: 'column', alignItems: 'stretch' }}>
                                        <div className="fin-pp__field" style={{ flex: '1 1 100%', minWidth: 0 }}><label className="fin-pp__label">Endpoint</label><span className="fin-pp__cell-muted" style={{ wordBreak: 'break-all' }}>{detalhe.http_method} {detalhe.url}</span></div>
                                        <div className="fin-pp__field" style={{ flex: '1 1 100%', minWidth: 0 }}><label className="fin-pp__label">Tela do front</label><span className="fin-pp__cell-muted" style={{ wordBreak: 'break-all' }}>{detalhe.link_acao || '-'}</span></div>
                                        <div className="fin-pp__field" style={{ flex: '1 1 100%', minWidth: 0 }}><label className="fin-pp__label">IP</label><span className="fin-pp__cell-muted">{detalhe.ip_address || '-'}</span></div>
                                        <div className="fin-pp__field" style={{ flex: '1 1 100%', minWidth: 0 }}><label className="fin-pp__label">Navegador</label><span className="fin-pp__cell-muted" style={{ wordBreak: 'break-all' }}>{normalizarUserAgent(detalhe.user_agent)}</span></div>
                                        <div className="fin-pp__field" style={{ flex: '1 1 100%', minWidth: 0 }}><label className="fin-pp__label">Trace</label><span className="fin-pp__cell-muted" style={{ wordBreak: 'break-all' }}>{detalhe.trace_id || '-'}</span></div>
                                    </div>
                                    <div className="fin-pp__tblwrap">
                                        <table className="fin-pp__tbl">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: 200 }}>Campo</th>
                                                    <th>Antes</th>
                                                    <th>Depois</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(Array.isArray(detalhe.campos) && detalhe.campos.length > 0) ? detalhe.campos.map((campo) => (
                                                    <tr key={campo}>
                                                        <td className="fin-pp__cell-strong">{campo}</td>
                                                        <td className="fin-pp__cell-muted"><ValorAudit campo={campo} valor={(detalhe.old_values || {})[campo]} situacoes={situacoes} /></td>
                                                        <td><ValorAudit campo={campo} valor={(detalhe.new_values || {})[campo]} situacoes={situacoes} /></td>
                                                    </tr>
                                                )) : (
                                                    <tr><td colSpan={3} className="fin-pp__empty">Sem alterações registradas</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <div className="fin-pp__modal-foot">
                                    <button type="button" className="fin-pp__btn" onClick={() => setDetalhe(null)}>Fechar</button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </ErpTemplate>
    );
}
